import { z } from 'zod';
import { CorrelationIdSchema } from './correlation.js';
/** Media type de las respuestas de error (RFC 9457). */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';
/** Código de error estable para clientes: MAYÚSCULAS_CON_GUION_BAJO (docs/migration/09 §3). */
export const ProblemCodeSchema = z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/);
/**
 * Códigos de docs/migration/09 §3 que ya usa la infraestructura, más los genéricos del filtro
 * de errores (`PAYLOAD_TOO_LARGE`, `HTTP_ERROR`). El esquema admite cualquier código con el
 * formato de `ProblemCodeSchema`, para que las fases siguientes añadan los de negocio.
 */
export const PROBLEM_CODES = {
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    FORBIDDEN: 'FORBIDDEN',
    CSRF_INVALID: 'CSRF_INVALID',
    NO_ACTIVE_EMPLOYEE: 'NO_ACTIVE_EMPLOYEE',
    NOT_FOUND: 'NOT_FOUND',
    VERSION_MISMATCH: 'VERSION_MISMATCH',
    IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
    PRECONDITION_REQUIRED: 'PRECONDITION_REQUIRED',
    // Fase 6 (09 §3 y §6.3 a §6.5): conflictos y reglas de negocio de áreas, empleados y roles.
    DUPLICATE: 'DUPLICATE',
    DIRECTOR_GENERAL_EXISTS: 'DIRECTOR_GENERAL_EXISTS',
    HIERARCHY_CYCLE: 'HIERARCHY_CYCLE',
    LAST_ADMINISTRATOR: 'LAST_ADMINISTRATOR',
    AREA_HAS_EMPLOYEES: 'AREA_HAS_EMPLOYEES',
    AREA_INACTIVE: 'AREA_INACTIVE',
    MANAGER_REQUIRED: 'MANAGER_REQUIRED',
    MANAGER_INVALID: 'MANAGER_INVALID',
    EMPLOYEE_INACTIVE: 'EMPLOYEE_INACTIVE',
    // Fase 7 (09 §3 y §6.6 a §6.8): solicitudes, calendario y catálogo.
    INVALID_TRANSITION: 'INVALID_TRANSITION',
    REQUEST_OVERLAP: 'REQUEST_OVERLAP',
    NON_WORKING_DAY: 'NON_WORKING_DAY',
    OUTSIDE_WORKDAY: 'OUTSIDE_WORKDAY',
    PAST_DATE: 'PAST_DATE',
    APPROVER_INVALID: 'APPROVER_INVALID',
    PERMISSION_TYPE_INACTIVE: 'PERMISSION_TYPE_INACTIVE',
    DOCUMENT_REQUIRED: 'DOCUMENT_REQUIRED',
    // Documentos (09 §6.11, D-07 y D-10).
    DOCUMENT_NOT_ALLOWED: 'DOCUMENT_NOT_ALLOWED',
    DOCUMENT_TYPE_NOT_ALLOWED: 'DOCUMENT_TYPE_NOT_ALLOWED',
    DOCUMENT_EXISTS: 'DOCUMENT_EXISTS',
    DOCUMENTS_UNAVAILABLE: 'DOCUMENTS_UNAVAILABLE',
    CALENDAR_NOT_CONFIGURED: 'CALENDAR_NOT_CONFIGURED',
    CALENDAR_NOT_PROSPECTIVE: 'CALENDAR_NOT_PROSPECTIVE',
    INVALID_TIME_ZONE: 'INVALID_TIME_ZONE',
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    HTTP_ERROR: 'HTTP_ERROR',
    INTERNAL: 'INTERNAL',
    UNAVAILABLE: 'UNAVAILABLE',
};
/**
 * Error de validación de un campo. `path` indica la ubicación (`body`, `query`, `params`,
 * `headers`) y la ruta dentro de ella, separadas por puntos; `code` es el código del validador
 * (p. ej. `too_small`). `message` es un texto fijo por código: nunca repite el valor recibido.
 */
export const ProblemFieldErrorSchema = z.strictObject({
    path: z.string().min(1).max(512),
    code: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    message: z.string().min(1).max(500),
});
/**
 * Cuerpo `application/problem+json` (RFC 9457) con las extensiones del contrato:
 * `code` (obligatorio), `traceId` (= identificador de correlación, obligatorio) y `errors`.
 *
 * - `type` es una referencia URI. Mientras la base de tipos de error esté pendiente (09 §3),
 *   la API usa `about:blank` y `title` es la frase del estado HTTP localizada (RFC 9457 §4.2.1).
 * - `instance` es la ruta de la petición **sin query string** (puede contener datos sensibles).
 * - Estricto: sin SQL, trazas, rutas locales ni propiedades adicionales.
 */
export const ProblemDetailsSchema = z.strictObject({
    type: z.string().min(1).max(2048),
    title: z.string().min(1).max(200),
    status: z.int().min(400).max(599),
    detail: z.string().min(1).max(1000).optional(),
    instance: z
        .string()
        .max(2048)
        .regex(/^\/[^?#\s]*$/)
        .optional(),
    code: ProblemCodeSchema,
    traceId: CorrelationIdSchema,
    errors: z.array(ProblemFieldErrorSchema).max(100).optional(),
});
//# sourceMappingURL=problem-details.js.map